<template>
  <div class="relative flex-1 cursor-text">
    <input
      :id="inputId"
      ref="input"
      v-model="content"
      v-maska
      :data-maska="dataMaska"
      :aria-invalid="hasErrorMessage"
      :aria-errormessage="hasErrorMessage ? `${inputId}-error` : undefined"
      class="invalid:border-red-500 out-of-range:border-red-500 read-only:to-gray-200 read-only:hover:border-secondary-700 peer w-full transform-gpu px-4 py-3 text-base ring-gray-tertiary transition-colors duration-200 read-only:cursor-not-allowed read-only:bg-gradient-to-b read-only:from-white focus:border-gray-secondary focus:ring-1 focus:ring-gray-secondary read-only:focus:ring-0"
      :class="{
        'placeholder:text-transparent': !maskLabel && !placeholder,
        'default:border-secondary-700 focus:border-secondary-700 hover:border-black':
          appearance === 'default',
        'border-red-500 default:border-red-500 hover:border-red-700':
          appearance === 'danger',
      }"
      :placeholder="isActive ? placeholder || maskLabel : ''"
      v-bind="{ required, readonly, type, autocomplete }"
      @focus="handleFocus"
      @blur="handleBlur"
    />
    <label
      :for="inputId"
      class="absolute left-2 min-w-max transform-gpu cursor-text bg-white px-2 transition-all duration-300"
      :class="[
        {
          '-top-2 text-xs font-bold': isActive,
          'top-3.5 text-base text-black-60': !isActive,
        },
      ]"
    >
      {{ label }} <span v-if="required" aria-hidden="true">*</span>
    </label>
  </div>
</template>

<script setup lang="ts">
import { vMaska } from 'maska'

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  type: {
    type: String as PropType<string>,
    default: 'text',
  },
  appearance: {
    type: String as PropType<'default' | 'danger'>,
    default: 'default',
    enum: ['default', 'danger'],
  },
  label: {
    type: String as PropType<string>,
    required: true,
  },
  placeholder: {
    type: String as PropType<string>,
    required: false,
  },
  inputId: {
    type: String as PropType<string>,
    required: true,
  },
  maskLabel: {
    type: String as PropType<string>,
    default: '',
  },
  autocomplete: {
    type: String as PropType<string>,
    default: undefined,
  },
  mask: {
    type: [String, Array] as PropType<string | string[]>,
    default: undefined,
  },
  required: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  readonly: {
    type: Boolean as PropType<boolean>,
    default: undefined,
  },
  min: {
    type: Number as PropType<number>,
    default: undefined,
  },
  max: {
    type: Number as PropType<number>,
    default: undefined,
  },
  maxLength: {
    type: Number as PropType<number>,
    default: undefined,
  },
  hasErrorMessage: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
})

const emit = defineEmits(['update:model-value', 'blur', 'focus'])

const content = computed({
  get: () => props.modelValue,
  set: (newValue: string): void => emit('update:model-value', newValue),
})

const dataMaska = computed(() => {
  // Maska don't accept array for the dynamic mask approach.
  // String conversion is needed in order to make it work properly
  return Array.isArray(props.mask) ? JSON.stringify(props.mask) : props.mask
})

const isFocused = ref(false)

const isActive = computed(() => {
  return isFocused.value || !!content.value
})

function handleBlur(event: FocusEvent) {
  isFocused.value = false
  emit('blur', event)
}

function handleFocus(event: FocusEvent) {
  isFocused.value = true
  emit('focus', event)
}
</script>
